// Deadline helpers shared by the candidate portal (role-level hiring process
// deadline) and the stage pages (per-candidate stage deadline). Deadlines are
// end-of-day UTC instants on the server.
//
// Mirrored in company-client `helpers/deadline.ts` — change both.

const API_DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Deadlines come through the API's date-only `Date` scalar; the server stores
 * them as the end of that UTC day, so a date-only value means "until
 * 23:59:59 UTC on that day". Full ISO timestamps pass through.
 */
export const parseApiDeadline = (
  value: string | Date | null | undefined
): Date | null => {
  if (!value) return null;
  if (typeof value === 'string' && API_DATE_ONLY.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d, 23, 59, 59));
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * The calendar day a deadline falls on, as a local Date at midnight — for
 * display. Deadlines are end-of-day UTC, so reading the UTC parts avoids the
 * instant rolling into the next local day east of UTC.
 */
export const deadlineCalendarDay = (instant: Date): Date =>
  new Date(
    instant.getUTCFullYear(),
    instant.getUTCMonth(),
    instant.getUTCDate()
  );

/**
 * The design's deadline stamp — "31 08 2026, 11:59pm UTC". The UTC parts are
 * formatted directly; a local-time format would shift the calendar day.
 */
export const formatDeadlineUtc = (instant: Date): string => {
  const day = String(instant.getUTCDate()).padStart(2, '0');
  const month = String(instant.getUTCMonth() + 1).padStart(2, '0');
  const year = instant.getUTCFullYear();
  const hours24 = instant.getUTCHours();
  const minutes = String(instant.getUTCMinutes()).padStart(2, '0');
  const meridiem = hours24 >= 12 ? 'pm' : 'am';
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  return `${day} ${month} ${year}, ${hours12}:${minutes}${meridiem} UTC`;
};

/** True once the deadline instant is behind us. */
export const isPastDeadline = (
  deadline: Date | null,
  now: Date = new Date()
): boolean => deadline !== null && deadline.getTime() < now.getTime();
